/**
 * Turns `ALL_POST_CONSULTATION_CONSULTS_QUERY` results into queue rows.
 *
 * Mirror of `preConsultationRows`, rooted on the consult instead of the intro
 * call. The two share their people-resolution and Active-lead assembly through
 * `listRowShared`, so a change to "who counts as the sender" cannot land on one
 * tab and miss the other.
 *
 * One row per PATIENT (BAR-2591). The query returns every APP and PN consult in
 * range; a patient with both is described by the latest one — its date, its
 * status, and its chart note's next step. See `latestConsultPerPatient`.
 */

import { getReferenceString } from '@medplum/core';
import { Coding } from '@medplum/fhirtypes';
import { ActiveLeadState } from '@bariendo/lib/active-lead';
import { Track } from '@bariendo/lib/constants';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { getPnConsultNextStep, noteForAppointment, parsePatientAccessCallAnswers } from '@bariendo/lib/pac-call-notes';
import { isPnConsultationAppointment } from '@bariendo/lib/patient-access';
import {
  formatNextStepCell,
  getNextSteps,
  getQuickNote,
  hasOverdueTask,
  isPostConsultationLead,
  latestConsultPerPatient,
} from '@bariendo/lib/pac-post-consultation-list';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import {
  activeLeadFor,
  ConsultAppointmentResult,
  firstPractitionerName,
  hydratedAppointments,
  LastCommunicationCell,
  StickyNoteCell,
  NextTaskCell,
  patientParticipant,
  PreConsultationPatientResult,
  toLastCommunicationCell,
  toStickyNoteCell,
  toNextTaskCell,
} from './listRowShared';
import { PacQueueHydration, hydratedPractitionerNames } from '../hooks/usePacQueueHydration';
import type { HumanName, Practitioner } from '@medplum/fhirtypes';

export type { NextTaskCell } from './listRowShared';

/** Nothing extra on the patient any more — it all arrives via bulk hydration. */
export type PostConsultationPatientResult = PreConsultationPatientResult;

export interface PostConsultationLeadRow {
  id: string;
  patient: PostConsultationPatientResult;
  /** The patient's latest live consult — NP or PN. */
  consult: ConsultAppointmentResult;
  /** Whoever ran the consult: the NP/PA, or the navigator for a PN consultation. */
  np?: HumanName;
  /** Every Patient Navigator on the CareTeam, in participant order. */
  patientNavigators: HumanName[];
  /** The same navigators as full records, so an avatar shows the photo without a fetch. */
  patientNavigatorPractitioners: Practitioner[];
  location?: string;
  /** The offering they are interested in, as stored: `ESG`, `IGB`, `TORe`. */
  procedure?: string;
  track?: Track;
  employer?: string;
  /**
   * The chart note's verdict on THIS consult, ready to render. An NP consult's
   * next-step answers (first + `+N`); a PN consultation's saved Outcome.
   */
  nextStep?: string;
  /** The NP next-step codings — the *Cleared to schedule* chip reads these. */
  nextSteps: Coding[];
  quickNote?: string;
  lastCommunication?: LastCommunicationCell;
  /** The patient's latest sticky note — the Intake hub's Notes column. */
  stickyNote?: StickyNoteCell;
  nextTask?: NextTaskCell;
  /** Any open task past its due date — drives the chip, not this row's cell. */
  hasOverdueTask: boolean;
  activeLead: ActiveLeadState;
}

function consultPatientRef(consult: ConsultAppointmentResult): string | undefined {
  const patient = patientParticipant(consult.participant);
  return patient?.id ? getReferenceString(patient) : undefined;
}

export function buildPostConsultationRows(
  consults: ConsultAppointmentResult[],
  hydration: PacQueueHydration,
  now: Date,
  /** The hub passes false; its Consultation no-show tab owns missed consults. */
  { includeNoShows = true }: { includeNoShows?: boolean } = {}
): PostConsultationLeadRow[] {
  const rows: PostConsultationLeadRow[] = [];

  // The query sorts `-date`, but a consult that arrived by subscription is
  // appended wherever the hook put it, so the order is re-established here:
  // otherwise a patient's consult from last week would keep the row while
  // today's sat at the end of the list, unseen.
  const newestFirst = [...consults].sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''));

  for (const consult of latestConsultPerPatient(newestFirst, consultPatientRef)) {
    const patient = patientParticipant(consult.participant);
    const patientRef = patient && getReferenceString(patient);

    if (!patient?.id || !patientRef || !consult.id) {
      continue;
    }

    const procedure = hydration.procedureByPatient.get(patientRef);

    if (!isPostConsultationLead({ consult, procedure }, now, { includeNoShows })) {
      continue;
    }

    const callNotes = hydration.callNotesByPatient.get(patientRef);
    const noteForThisConsult = noteForAppointment(
      callNotes,
      getReferenceString({ resourceType: 'Appointment', id: consult.id })
    );
    // Location: the rep's explicit edit, then this consult's card, then whatever
    // intake card exists (an intro call before a PN consult, say).
    const locationNote = noteForThisConsult ?? callNotes?.[0];
    const consultNotes = hydration.consultNotesByPatient.get(patientRef);
    const openTasks = hydration.tasksByPatient.get(patientRef);
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);
    const nextSteps = getNextSteps(consultNotes);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );
    const locationAnswers = locationNote ? parsePatientAccessCallAnswers(locationNote) : undefined;
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const eligibilitySelections = readEligibilityIntakeSelections(eligibility);
    const episode = hydration.episodeByPatient.get(patientRef);

    rows.push({
      id: consult.id,
      patient,
      consult,
      np: firstPractitionerName(consult.participant),
      patientNavigators: hydratedPractitionerNames(navigatorRefs, hydration.practitionerByRef),
      patientNavigatorPractitioners: navigatorRefs
        .map((reference) => hydration.practitionerByRef.get(reference))
        .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
      // One reader across the panel, the card and both list tabs — see the note
      // in preConsultationRows. The eligibility market is the third link, for a
      // lead whose market was never charted on a call.
      location: readMarketLocation(patient, locationAnswers, eligibilitySelections.market),
      procedure: readInterestedService(patient, locationAnswers, eligibilitySelections.procedure),
      track: getTrackOrSelfPay(episode),
      employer: readEmployer(patient) ?? readEligibilityPayment(eligibility).employer,
      nextStep: isPnConsultationAppointment(consult)
        ? getPnConsultNextStep(noteForThisConsult)
        : formatNextStepCell(nextSteps) || undefined,
      nextSteps,
      quickNote: getQuickNote(consultNotes),
      lastCommunication: toLastCommunicationCell(lastCommunication),
      stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      nextTask: toNextTaskCell(openTasks, hydration.practitionerByRef),
      hasOverdueTask: hasOverdueTask(openTasks, now),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: episode?.period?.start,
        appointments: [consult, ...hydratedAppointments(hydration, patientRef)],
        now,
      }),
    });
  }

  return rows;
}
